import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';

import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { NXDataSourcePrime } from '../../services/dataSource/NXDataSourcePrime';
import { LoadingService } from '../../services/loading.service';
import { RolesService } from '../../services/roles/roles.service';
import { TenantsService } from '../../services/tenants/tenants.service';
import { EditDialogService } from '../editDialog/editDialogService';
import { MobileDeviceDataSource } from './mobileDeviceDataSource';
import { AppVersionsDialogComponent } from '../app-versions-dialog/app-versions-dialog.component';
import { ToastService } from '../../services/toast/toast.service';
import { ConnectedDeviceSettingsService } from '../../services/connectedDeviceSettings/connected-device-settings.service';
import { forkJoin, take } from 'rxjs';
import { ConnectedDeviceAppVersions } from '../../entities/connectedDeviceSetting/connectedDeviceSetting';
import { TranslateService } from '@ngx-translate/core';
import { ConnectedDevice } from '../../entities/connected-device/connected-device';

@Component({
  selector: 'app-mobile-device-list',
  templateUrl: './mobile-device-list.component.html',
  styleUrls: ['./mobile-device-list.component.scss']
})
export class MobileDeviceListComponent implements OnDestroy, OnInit {

  dialogRef: DynamicDialogRef | undefined;

  public sortProperty: string;
  public sortDirection: string;
  public pageNumber = 0;
  public pageSize = 15;
  public text: string | undefined;
  
  public sortOptions = [
    { label: 'coreModule.mobileDeviceListComponent.deviceList.sorting.noSort', fieldName: 'id', direction: "asc", key: 'noSort'},
    { label: 'coreModule.mobileDeviceListComponent.deviceList.sorting.lastSeenAsc', fieldName: 'lastSeenDateTime', direction: "asc", key: 'lastSeenDateTimeAsc'},
    { label: 'coreModule.mobileDeviceListComponent.deviceList.sorting.lastSeenDesc', fieldName: 'lastSeenDateTime', direction: "desc", key: 'lastSeenDateTimeDesc' },
    { label: 'coreModule.mobileDeviceListComponent.deviceList.sorting.createdDateTimeAsc', fieldName: 'createdDateTime', direction: "asc", key: 'createdDateTimeAsc' },
    { label: 'coreModule.mobileDeviceListComponent.deviceList.sorting.createdDateTimeDesc', fieldName: 'createdDateTime', direction: "desc", key: 'createdDateTimeDesc' }
  ];
  public sortModel = this.sortOptions[2];
  public defaultSortProperty: string = this.sortOptions[2]?.fieldName;
  public defaultSortDirection: string = this.sortOptions[2]?.direction;

  public dataSource: MobileDeviceDataSource;
  public loading = false;

  public androidAppVersions: ConnectedDeviceAppVersions | undefined;
  public iosAppVersions: ConnectedDeviceAppVersions | undefined;

  constructor(
    private router: Router,
    private apollo: Apollo,
    public tenantService:TenantsService,
    public rolesService: RolesService,
    private cdRef: ChangeDetectorRef,
    public dialogService: EditDialogService,
    public loadingService: LoadingService,
    @Inject('env') private environment : any,
    private toastService: ToastService,
    private connectedDeviceSettingsService: ConnectedDeviceSettingsService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.dataSource = new MobileDeviceDataSource(this.apollo, this.environment);
    this.dataSource.loading.subscribe((loading) => {
      this.loading = loading;
    });
    this.dataSource.loadDevices({
      pageNumber: 0,
      pageSize: 15,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
      type: 'APP',
      text: this.text
    });
    forkJoin({
      ios: this.connectedDeviceSettingsService.getAppVersions("IOS").pipe(take(1)),
      android: this.connectedDeviceSettingsService.getAppVersions("ANDROID").pipe(take(1))
    }).subscribe(({ ios, android }) => {
      this.iosAppVersions = ios;
      this.androidAppVersions = android;
    });
  }

  refetchTenants(force = false): void {
    this.dataSource.refetchQuery({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
      type: 'APP',
      text: this.text
    }, force);
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  loadData(event: TableLazyLoadEvent) {
    this.loading = true;

    // sorting
    if (event?.sortField !== undefined && typeof event?.sortField == 'string') {
      this.sortProperty = event.sortField;
    }
    if (event?.sortOrder !== undefined) {
      this.sortDirection = NXDataSourcePrime.translateSortOrderToSortDirection(event.sortOrder);
    }

    // pagination
    this.pageNumber = event?.first !== undefined ? event.first / event.rows : 0;
    this.pageSize = event?.rows !== undefined ? event.rows : 15;

    if(event?.globalFilter !== undefined || event?.globalFilter !== null) {
      this.text = event.globalFilter as string;
    } else {
      this.text = undefined;
    }
    this.refetchTenants();
  }

  sort(event: any) {
    if(event?.value?.direction && event?.value?.fieldName){
      this.sortProperty = event.value.fieldName;
      this.sortDirection = event.value.direction;
    } else {
      this.sortProperty = this.defaultSortProperty;
      this.sortDirection = this.defaultSortDirection;
    }
    this.cdRef.detectChanges();
  }

  translateSortDirectionToSortOrder(sortDirection: string): number {
    return NXDataSourcePrime.translateSortDirectionToSortOrder(sortDirection);
  }

  calculateSeverity(updateState: string): "success" | "secondary" | "info" | "warning" | "danger" | "contrast" {
   // NO_UPDATE, SHOULD_UPDATE, MUST_UPDATE
   switch (updateState) {
      case 'NO_UPDATE':
        return 'success';
      case 'SHOULD_UPDATE':
        return 'warning';
      case 'MUST_UPDATE':
        return 'danger';
      default:
        return 'success';
    }
  }

  updateLabel(updateState: string): string {
    // NO_UPDATE, SHOULD_UPDATE, MUST_UPDATE
    switch (updateState) { 
      case 'NO_UPDATE':
        return 'coreModule.mobileDeviceListComponent.deviceList.updateState.noUpdate';
      case 'SHOULD_UPDATE':
        return 'coreModule.mobileDeviceListComponent.deviceList.updateState.shouldUpdate';
      case 'MUST_UPDATE':
        return 'coreModule.mobileDeviceListComponent.deviceList.updateState.mustUpdate';
      default:
        return 'coreModule.mobileDeviceListComponent.deviceList.updateState.noUpdate';
    }
  }

  isAdmin(): boolean {
    return this.rolesService.hasRole('framework_tenants_manage');
  }

  updateAppVersions(): void { 
      this.dialogRef = this.dialogService.open(AppVersionsDialogComponent, {
          header: 'coreModule.mobileDeviceListComponent.updateAppVersionsDialogComponentHeader'
      });
      this.dialogRef.onClose.subscribe((result) => {
        // currently the page just reloads in case something has change
        // this is handled in the AppVersionsDialogComponent
      });
  }

  openGooglePlayStore(): void {
    window.open('https://play.google.com/store/apps/details?id=de.nexato.nexcore.frontend.app', '_blank');
  }

  openAppleAppStore(): void {
    window.open('https://apps.apple.com/de/app/nexcore/id6474186816', '_blank');  
  }

  getPlaceholder(): string {
    return this.translate.instant('coreModule.mobileDeviceListComponent.deviceList.sorting.placeholder')
  }

  openMobileDevice(mobileDevice: ConnectedDevice): void {
    this.router.navigate(['./settings/mobileDevices/mobileDevice/', mobileDevice.id]);
  }
}
